import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { generateDeviceTrackingData, saveTrackingConsent } from './utils/deviceFingerprint';
import SessionLimitModal from './components/SessionLimitModal';
// Phase 7 sign-off: profiles email, password, display_name.
import { MAX_LENGTHS, useCappedPaste } from './utils/inputValidation';
import { isFieldOk } from './utils/inputFormHelpers';

export default function LoginPage() {
  const { login, register } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/app';
  const isKo = i18n.language === 'ko';

  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [ethicsAccepted, setEthicsAccepted] = useState(false);
  const [trackingConsent, setTrackingConsent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessionLimitData, setSessionLimitData] = useState(null);
  const [touched, setTouched] = useState({});
  const markTouched = (k) => setTouched((t) => ({ ...t, [k]: true }));

  // Phase 7: bound paste handlers per field (referentially stable).
  const onEmailPaste     = useCappedPaste(MAX_LENGTHS.email);
  const onPasswordPaste  = useCappedPaste(MAX_LENGTHS.password);
  const onFullNamePaste  = useCappedPaste(MAX_LENGTHS.display_name);
  const onOrgNamePaste   = useCappedPaste(MAX_LENGTHS.display_name);

  const emailResult    = isFieldOk(email,    'email');
  const passwordResult = isFieldOk(password, 'password');
  const fullNameResult = isFieldOk(fullName, 'display_name');
  const orgNameResult  = isFieldOk(orgName,  'display_name');
  const loginGateOk    = emailResult.ok && passwordResult.ok;
  const registerGateOk = loginGateOk && fullNameResult.ok && orgNameResult.ok && ethicsAccepted;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('signup') === 'true') setMode('register');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const switchLang = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      if (err.type === 'session_limit') {
        setSessionLimitData(err.data);
        setShowSessionModal(true);
        setLoading(false);
        return;
      }
      // A tagged 401 is rendered in the user's language rather than echoing the
      // server's English detail; anything else still shows what the server said.
      setError(err.code === 'INVALID_CREDENTIALS'
        ? t('auth.invalidCredentials')
        : (err.message || t('auth.invalidCredentials')));
    } finally {
      setLoading(false);
    }
  };

  const handleEndAndContinue = async (sessionId) => {
    setShowSessionModal(false);
    setSessionLimitData(null);
    setLoading(true);
    try {
      await login(email, password, sessionId);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed after ending session');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!ethicsAccepted) {
      setError(isKo ? '이용약관 및 개인정보처리방침에 동의해주세요' : 'Please accept the Terms of Service and Privacy Policy');
      return;
    }
    if (confirmPassword && password !== confirmPassword) {
      setError(isKo ? '비밀번호가 일치하지 않습니다' : 'Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const deviceData = trackingConsent ? await generateDeviceTrackingData() : null;
      if (trackingConsent) saveTrackingConsent(true);
      await register(email, password, fullName, orgName, deviceData, isKo ? 'ko' : 'en');
      // P1-A: All new sign-ups land on the pending screen — both for email
      // verification and admin activation. Dashboard access is gated by
      // ProtectedRoute until both are complete.
      navigate('/signup/pending', { replace: true, state: { email } });
    } catch (err) {
      // G1: an existing but UNVERIFIED account is not a dead end. The backend
      // has just re-sent the verification code, so send the user to the same
      // pending screen a fresh signup reaches instead of showing
      // "Email already registered" on the one form they'd keep retrying.
      if (err?.code === 'ACCOUNT_EXISTS_UNVERIFIED') {
        navigate('/signup/pending', {
          replace: true,
          state: { email, resent: err.verificationEmailSent !== false },
        });
        return;
      }
      setError(err.message || t('auth.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Phase 7: submit-gate folds in validateText through isFieldOk.
  const canSubmitRegister = registerGateOk;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative p-6" style={{ backgroundColor: '#0D1117' }}>
      {/* Glow overlay */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
        <div style={{ position: 'absolute', top: '-10%', right: '-5%', width: '40%', height: '40%', background: 'radial-gradient(circle, rgba(161,0,170,0.15) 0%, rgba(161,0,170,0) 70%)', filter: 'blur(80px)' }} />
        <div style={{ position: 'absolute', bottom: '-10%', left: '-5%', width: '40%', height: '40%', background: 'radial-gradient(circle, rgba(0,51,153,0.2) 0%, rgba(0,51,153,0) 70%)', filter: 'blur(80px)' }} />
      </div>

      {/* Language toggle top-right */}
      <div className="fixed top-6 right-6 z-50">
        <div className="flex items-center" style={{ border: '1px solid rgba(255,255,255,0.15)', borderRadius: 20, padding: 2 }}>
          <button onClick={() => switchLang('ko')} className="text-xs font-bold transition-all" style={{ padding: '4px 12px', borderRadius: 20, background: isKo ? '#a100aa' : 'transparent', color: isKo ? '#fff' : 'rgba(255,255,255,0.5)' }}>KR</button>
          <button onClick={() => switchLang('en')} className="text-xs font-bold transition-all" style={{ padding: '4px 12px', borderRadius: 20, background: !isKo ? '#a100aa' : 'transparent', color: !isKo ? '#fff' : 'rgba(255,255,255,0.5)' }}>EN</button>
        </div>
      </div>

      {/* Main card */}
      <main className="w-full max-w-[420px] relative z-10">
      <div style={{ backgroundColor: '#161B22', border: '1px solid #30363D', borderRadius: 20, boxShadow: '0 24px 48px rgba(0,0,0,0.4)', overflow: 'hidden' }}>
        {/* Brand header */}
        <div className="text-center" style={{ paddingTop: 40, paddingBottom: 32, paddingLeft: 32, paddingRight: 32 }}>
          <h1 className="font-headline font-extrabold text-white uppercase" style={{ fontSize: 28, letterSpacing: '-0.5px', marginBottom: 6 }}>VERIPASS</h1>
          <p style={{ fontSize: 14, color: '#94a3b8', fontWeight: 500 }}>{isKo ? '화장품 규정 준수 검증 서비스' : 'Cosmetic Compliance Verification Service'}</p>
        </div>

        {/* Tab switcher */}
        <div className="flex" style={{ backgroundColor: '#0a0e14', borderRadius: 12, padding: 4, marginLeft: 32, marginRight: 32, marginBottom: 32 }}>
          <button
            onClick={() => { setMode('login'); setError(''); }}
            className="flex-1 font-headline font-bold transition-all"
            style={{ padding: '10px 0', borderRadius: 8, fontSize: 14, background: mode === 'login' ? '#a100aa' : 'transparent', color: mode === 'login' ? '#fff' : '#94a3b8', boxShadow: mode === 'login' ? '0 4px 12px rgba(161,0,170,0.3)' : 'none' }}
          >
            {isKo ? '로그인' : 'Sign In'}
          </button>
          <button
            onClick={() => { setMode('register'); setError(''); }}
            className="flex-1 font-headline font-bold transition-all"
            style={{ padding: '10px 0', borderRadius: 8, fontSize: 14, background: mode === 'register' ? '#a100aa' : 'transparent', color: mode === 'register' ? '#fff' : '#94a3b8', boxShadow: mode === 'register' ? '0 4px 12px rgba(161,0,170,0.3)' : 'none' }}
          >
            {isKo ? '조직 생성' : 'Create Org'}
          </button>
        </div>

        <div style={{ paddingLeft: 32, paddingRight: 32, paddingBottom: 40 }}>
        {error && (
          <div className="mb-4 text-sm" style={{ color: '#F87171', backgroundColor: 'rgba(185,28,28,0.1)', border: '1px solid rgba(185,28,28,0.3)', borderRadius: 8, padding: '10px 14px' }}>
            {error}
          </div>
        )}

        {mode === 'login' ? (
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block mb-1.5 ml-1" style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {isKo ? '이메일 주소' : 'Email'}
              </label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} onBlur={() => markTouched('email')} onPaste={onEmailPaste} maxLength={MAX_LENGTHS.email} placeholder="name@company.com" required autoFocus autoComplete="email" className="vp-input" />
              {touched.email && email.length > 0 && !emailResult.ok && (
                <p className="mt-1 ml-1" style={{ fontSize: 11, color: '#F87171' }}>{emailResult.message}</p>
              )}
            </div>
            <div>
              <label className="block mb-1.5 ml-1" style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {isKo ? '비밀번호' : 'Password'}
              </label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required className="vp-input" />
              <div className="flex justify-end mt-1.5 mr-1">
                <Link to="/auth/forgot-password" style={{ fontSize: 12, color: '#6699FF' }} className="hover:underline">
                  {isKo ? '비밀번호 찾기' : 'Forgot password?'}
                </Link>
              </div>
            </div>

            <button type="submit" disabled={loading || !loginGateOk} className="w-full font-headline font-extrabold text-white transition-all active:scale-[0.98]" style={{ backgroundColor: '#a100aa', padding: '15px', borderRadius: 12, fontSize: 15, border: 'none', cursor: 'pointer', marginTop: 12, opacity: (loading || !loginGateOk) ? 0.6 : 1, boxShadow: '0 8px 24px rgba(161,0,170,0.2)' }}
              onMouseEnter={e => { if (!loading) e.target.style.backgroundColor = '#b800c2'; }}
              onMouseLeave={e => { e.target.style.backgroundColor = '#a100aa'; }}>
              {loading ? (isKo ? '로그인 중...' : 'Signing in...') : (isKo ? '로그인' : 'Sign In')}
            </button>

            <div className="pt-5 text-center">
              <p style={{ fontSize: 13, color: '#6B7280', fontWeight: 500 }}>
                {isKo ? '계정이 없으신가요? ' : "Don't have an account? "}
                <span onClick={() => { setMode('register'); setError(''); }} style={{ color: '#ffa9fa', fontWeight: 700, cursor: 'pointer' }} className="hover:underline">
                  {isKo ? '조직 신청하기' : 'Create Organization'}
                </span>
              </p>
            </div>
          </form>
        ) : (
          <form onSubmit={handleRegister} className="space-y-5">
            <div>
              <label className="block mb-1.5 ml-1" style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{isKo ? '이름' : 'Name'}</label>
              <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} onBlur={() => markTouched('fullName')} onPaste={onFullNamePaste} maxLength={MAX_LENGTHS.display_name} placeholder={isKo ? '홍길동' : 'Full name'} required autoComplete="name" className="vp-input" />
              {touched.fullName && fullName.length > 0 && !fullNameResult.ok && (
                <p className="mt-1 ml-1" style={{ fontSize: 11, color: '#F87171' }}>{fullNameResult.message}</p>
              )}
            </div>
            <div>
              <label className="block mb-1.5 ml-1" style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{isKo ? '이메일' : 'Email'}</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} onBlur={() => markTouched('email')} onPaste={onEmailPaste} maxLength={MAX_LENGTHS.email} placeholder="abc@domain.kr" required autoComplete="email" className="vp-input" />
              {touched.email && email.length > 0 && !emailResult.ok && (
                <p className="mt-1 ml-1" style={{ fontSize: 11, color: '#F87171' }}>{emailResult.message}</p>
              )}
            </div>
            <div>
              <label className="block mb-1.5 ml-1" style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{isKo ? '비밀번호' : 'Password'}</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} onBlur={() => markTouched('password')} onPaste={onPasswordPaste} maxLength={MAX_LENGTHS.password} placeholder="••••••••" required minLength={8} autoComplete="new-password" className="vp-input" />
              {touched.password && password.length > 0 && !passwordResult.ok && (
                <p className="mt-1 ml-1" style={{ fontSize: 11, color: '#F87171' }}>{passwordResult.message}</p>
              )}
              <p className="mt-1 ml-1" style={{ fontSize: 11, color: '#6B7280' }}>{isKo ? '8자 이상, 영문+숫자 포함' : '8+ characters, letters + numbers'}</p>
            </div>
            <div>
              <label className="block mb-1.5 ml-1" style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{isKo ? '비밀번호 확인' : 'Confirm Password'}</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} onPaste={onPasswordPaste} maxLength={MAX_LENGTHS.password} placeholder="••••••••" autoComplete="new-password" className="vp-input" style={confirmPassword && password !== confirmPassword ? { borderColor: '#B91C1C' } : {}} />
              {confirmPassword && password !== confirmPassword && (
                <p className="mt-1 ml-1" style={{ fontSize: 11, color: '#F87171' }}>{isKo ? '비밀번호가 일치하지 않습니다' : 'Passwords do not match'}</p>
              )}
            </div>
            <div>
              <label className="block mb-1.5 ml-1" style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{isKo ? '조직명' : 'Organization'}</label>
              <input type="text" value={orgName} onChange={e => setOrgName(e.target.value)} onBlur={() => markTouched('orgName')} onPaste={onOrgNamePaste} maxLength={MAX_LENGTHS.display_name} placeholder={isKo ? '회사 또는 브랜드명' : 'Company or brand name'} required autoComplete="organization" className="vp-input" />
              {touched.orgName && orgName.length > 0 && !orgNameResult.ok && (
                <p className="mt-1 ml-1" style={{ fontSize: 11, color: '#F87171' }}>{orgNameResult.message}</p>
              )}
            </div>

            {/* Checkboxes */}
            <div className="space-y-3 pt-2">
              <label className="flex items-start gap-2 cursor-pointer" style={{ fontSize: 12, color: '#94a3b8' }}>
                <input type="checkbox" checked={ethicsAccepted} onChange={e => setEthicsAccepted(e.target.checked)} className="mt-0.5" style={{ accentColor: '#a100aa', width: 16, height: 16 }} />
                <span>
                  <Link to="/about#terms" target="_blank" style={{ color: '#6699FF' }}>{isKo ? '이용약관' : 'Terms of Service'}</Link>
                  {isKo ? ' 및 ' : ' and '}
                  <Link to="/about#privacy" target="_blank" style={{ color: '#6699FF' }}>{isKo ? '개인정보처리방침' : 'Privacy Policy'}</Link>
                  {isKo ? '에 동의합니다' : ' — I agree'}
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer" style={{ fontSize: 12, color: '#94a3b8' }}>
                <input type="checkbox" checked={trackingConsent} onChange={e => setTrackingConsent(e.target.checked)} className="mt-0.5" style={{ accentColor: '#a100aa', width: 16, height: 16 }} />
                <span>
                  {isKo ? '장치 추적에 동의합니다 (보안 강화 목적)' : 'I consent to device tracking (for security)'}{' '}
                  <Link to="/about#privacy" target="_blank" style={{ color: '#6699FF' }}>{isKo ? '자세히 보기 →' : 'Learn more →'}</Link>
                </span>
              </label>
            </div>

            <button type="submit" disabled={loading || !canSubmitRegister} className="w-full font-headline font-extrabold text-white transition-all active:scale-[0.98]" style={{ backgroundColor: '#a100aa', padding: '15px', borderRadius: 12, fontSize: 15, border: 'none', cursor: 'pointer', marginTop: 12, opacity: (loading || !canSubmitRegister) ? 0.35 : 1, pointerEvents: (loading || !canSubmitRegister) ? 'none' : 'auto', boxShadow: '0 8px 24px rgba(161,0,170,0.2)' }}
              onMouseEnter={e => { if (!loading && canSubmitRegister) e.target.style.backgroundColor = '#b800c2'; }}
              onMouseLeave={e => { e.target.style.backgroundColor = '#a100aa'; }}>
              {loading ? (isKo ? '생성 중...' : 'Creating...') : (isKo ? '계정 만들기' : 'Create Account')}
            </button>
          </form>
        )}
        </div>
      </div>

      {/* Footer link */}
      <div className="mt-8 text-center">
        <Link to="/" className="group inline-flex items-center gap-2 transition-colors" style={{ color: '#6B7280', textDecoration: 'none', fontSize: 14, fontWeight: 700 }}
          onMouseEnter={e => e.currentTarget.style.color = '#fff'}
          onMouseLeave={e => e.currentTarget.style.color = '#6B7280'}>
          <span style={{ transition: 'transform 0.2s' }}>←</span>
          <span>{isKo ? '홈으로 돌아가기' : 'Return to Home'}</span>
        </Link>
      </div>
      </main>

      {/* Bottom accent bar */}
      <div className="fixed pointer-events-none" style={{ bottom: 40, left: '50%', transform: 'translateX(-50%)', opacity: 0.2 }}>
        <div style={{ width: 256, height: 4, background: 'linear-gradient(to right, transparent, #ffa9fa, transparent)', filter: 'blur(2px)' }} />
      </div>

      {showSessionModal && sessionLimitData && (
        <SessionLimitModal
          activeSessions={sessionLimitData.active_sessions || []}
          maxAllowed={sessionLimitData.max_allowed || 3}
          onEndAndContinue={handleEndAndContinue}
          onCancel={() => { setShowSessionModal(false); setSessionLimitData(null); }}
        />
      )}
    </div>
  );
}
